import { OnboardingData, StudyPlan } from '../types/onboarding';

const PRE_AUTH_WIZARD_KEY = 'preAuthWizardData';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PRE_AUTH_PLAN_KEY = 'preAuthStudyPlan';

export interface PreAuthWizardState {
    onboardingData: Partial<OnboardingData>;
    studyPlan: StudyPlan | null;
    completedAt: string;
}

/**
 * Saves wizard progress to localStorage before user creates account
 */
export const savePreAuthWizardData = (data: Partial<OnboardingData>): void => {
    try {
        const existing = getPreAuthWizardData();
        const updated: PreAuthWizardState = {
            onboardingData: {
                ...(existing?.onboardingData || {}),
                ...data,
                completedAt: new Date().toISOString(),
            },
            studyPlan: existing?.studyPlan || null,
            completedAt: new Date().toISOString(),
        };
        localStorage.setItem(PRE_AUTH_WIZARD_KEY, JSON.stringify(updated));
        console.log('[PreAuthCache] Saved wizard data:', Object.keys(data));
    } catch (error) {
        console.error('[PreAuthCache] Error saving wizard data:', error);
    }
};

/**
 * Saves the generated study plan to localStorage
 */
export const savePreAuthStudyPlan = (plan: StudyPlan): void => {
    try {
        const existing = getPreAuthWizardData();
        const updated: PreAuthWizardState = {
            ...existing,
            onboardingData: existing?.onboardingData || {},
            studyPlan: plan,
            completedAt: new Date().toISOString(),
        };
        localStorage.setItem(PRE_AUTH_WIZARD_KEY, JSON.stringify(updated));
        console.log('[PreAuthCache] Saved study plan');
    } catch (error) {
        console.error('[PreAuthCache] Error saving study plan:', error);
    }
};

/**
 * Retrieves pre-auth wizard data from localStorage
 */
export const getPreAuthWizardData = (): PreAuthWizardState | null => {
    try {
        const data = localStorage.getItem(PRE_AUTH_WIZARD_KEY);
        if (data) {
            return JSON.parse(data) as PreAuthWizardState;
        }
        return null;
    } catch (error) {
        console.error('[PreAuthCache] Error reading wizard data:', error);
        return null;
    }
};

/**
 * Checks if there's pre-auth wizard data waiting to be transferred
 */
export const hasPreAuthWizardData = (): boolean => {
    const data = getPreAuthWizardData();
    return data !== null && data.studyPlan !== null;
};

/**
 * Clears pre-auth wizard data from localStorage
 * Call this after successfully transferring to user account
 */
export const clearPreAuthWizardData = (): void => {
    try {
        localStorage.removeItem(PRE_AUTH_WIZARD_KEY);
        console.log('[PreAuthCache] Cleared pre-auth wizard data');
    } catch (error) {
        console.error('[PreAuthCache] Error clearing wizard data:', error);
    }
};

/**
 * Transfers pre-auth data to authenticated user
 * Returns the data to be saved, or null if no data exists
 */
export const getTransferablePreAuthData = (): PreAuthWizardState | null => {
    const data = getPreAuthWizardData();
    if (data && data.studyPlan) {
        return data;
    }
    return null;
};
